import { useState, useCallback, useMemo } from 'react';
import { useData } from '../../../contexts/DataContext';

/**
 * Custom hook for managing client lists with filtering, search, and operations
 * 
 * Provides client list data, filtering, search, and client management operations
 * without any UI concerns.
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.filters - Initial filter values
 * @returns {Object} Client list data, filters, search, and actions
 */
export const useClientList = (options = {}) => {
	const {
		filters: initialFilters = {},
	} = options;

	const { clients, loading, error, fetchClients, createClient, createAccount } = useData();

	const [searchTerm, setSearchTerm] = useState('');
	const [selectedPlatform, setSelectedPlatform] = useState('');
	const [sortBy, setSortBy] = useState('risk');
	const [activeTab, setActiveTab] = useState('all');
	const [filters, setFilters] = useState({
		category: '',
		risk: '',
		lastScan: '',
		...initialFilters,
	});

	// Calculate statistics
	// A client "needs attention" when its rolled-up risk is non-trivial OR its
	// coverage has gone stale - an unscanned celebrity is not a safe one, it is
	// an unwatched one. Same rule as the account cards' staleness hint.
	const STALE_DAYS = 14;
	const needsAttention = (client) => {
		if ((client.risk || 0) >= 40) return true;
		if (!client.lastScan) return true;
		return (Date.now() - new Date(client.lastScan).getTime()) / 86400000 > STALE_DAYS;
	};

	const stats = useMemo(() => ({
		totalClients: clients.length,
		attentionClients: clients.filter(needsAttention).length,
		neverScanned: clients.filter((client) => !client.lastScan).length,
	}), [clients]);

	// Filter and search clients
	const filteredClients = useMemo(() => {
		let filtered = [...clients];

		// Apply tab filter
		switch (activeTab) {
			case 'attention':
				filtered = filtered.filter(needsAttention);
				break;
			case 'unscanned':
				filtered = filtered.filter((client) => !client.lastScan);
				break;
			case 'all':
			default:
				break;
		}

		// Apply search filter
		if (searchTerm) {
			const term = searchTerm.toLowerCase();
			filtered = filtered.filter((client) =>
				client.name.toLowerCase().includes(term) ||
				client.email?.toLowerCase().includes(term) ||
				client.category?.toLowerCase().includes(term)
			);
		}

		// Apply platform filter
		if (selectedPlatform) {
			filtered = filtered.filter((client) =>
				client.accounts?.some((account) => account.platform === selectedPlatform)
			);
		}

		// Apply additional filters
		if (filters.category) {
			filtered = filtered.filter((client) => client.category === filters.category);
		}

		if (filters.risk) {
			const riskLevel = parseInt(filters.risk);
			filtered = filtered.filter((client) => client.risk >= riskLevel);
		}

		// Apply sorting
		filtered.sort((a, b) => {
			switch (sortBy) {
				case 'name':
					return a.name.localeCompare(b.name);
				case 'risk':
					return (b.risk || 0) - (a.risk || 0);
				case 'lastScan':
					return new Date(b.lastScan || 0) - new Date(a.lastScan || 0);
				case 'category':
					return (a.category || '').localeCompare(b.category || '');
				default:
					return 0;
			}
		});

		return filtered;
	}, [clients, activeTab, searchTerm, selectedPlatform, filters, sortBy]);

	// Client management actions
	const addClient = useCallback(async (clientData) => {
		try {
			await createClient(clientData);
			await fetchClients(); // Refresh the list
			return { success: true };
		} catch (error) {
			console.error('Error creating client:', error);
			throw error;
		}
	}, [createClient, fetchClients]);

	const addAccount = useCallback(async (clientId, accountData) => {
		try {
			await createAccount({ ...accountData, client: clientId });
			await fetchClients(); // Refresh the list
			return { success: true };
		} catch (error) {
			console.error('Error creating account:', error);
			throw error;
		}
	}, [createAccount, fetchClients]);

	// Update search and filters
	const updateSearch = useCallback((term) => {
		setSearchTerm(term);
	}, []);

	const updateFilters = useCallback((newFilters) => {
		setFilters(prev => ({ ...prev, ...newFilters }));
	}, []);

	const updateSorting = useCallback((newSortBy) => {
		setSortBy(newSortBy);
	}, []);

	const updateTab = useCallback((tab) => {
		setActiveTab(tab);
	}, []);

	const updatePlatformFilter = useCallback((platform) => {
		setSelectedPlatform(platform);
	}, []);

	// Reset all filters
	const resetFilters = useCallback(() => {
		setSearchTerm('');
		setSelectedPlatform('');
		setSortBy('lastScan');
		setActiveTab('all');
		setFilters({
			category: '',
			risk: '',
			lastScan: '',
			...initialFilters,
		});
	}, [initialFilters]);

	// Refresh data
	const refresh = useCallback(async () => {
		await fetchClients();
	}, [fetchClients]);

	// Computed values
	const hasFilters = searchTerm || selectedPlatform || Object.values(filters).some(v => v !== '');
	const isEmpty = !loading && filteredClients.length === 0;
	const isFiltered = hasFilters && isEmpty;

	return {
		// Data
		clients: filteredClients,
		allClients: clients,
		loading,
		error,
		stats,

		// Search and filters
		searchTerm,
		selectedPlatform,
		sortBy,
		activeTab,
		filters,
		hasFilters,

		// Actions
		addClient,
		addAccount,
		updateSearch,
		updateFilters,
		updateSorting,
		updateTab,
		updatePlatformFilter,
		resetFilters,
		refresh,

		// Computed states
		isEmpty,
		isFiltered,
	};
}; 